import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DeviceChipOptions, DeviceOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';

import { MatChipInputEvent } from '@angular/material/chips';
import { PhoneTypes } from 'generated/graphql-types';
import { BaseStationGroupSelectComponent } from '../component/basestation-select.component';
import { ChipInputOption, IncomingBaseStationData } from './basestation.model';

@Component({
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: BasestationChipInputComponent }],
    selector: 'ndt-basestation-chip-input',
    styleUrls: ['./basestation-chip-input.component.scss'],
    templateUrl: './basestation-chip-input.component.html',
    standalone: false
})
export class BasestationChipInputComponent implements ControlValueAccessor, OnInit {
   @Input() required: boolean = false;

   @Input() set devices(devices: DeviceChipOptions[]) {
      if (devices !== undefined) {
         const combinedPhones = devices
            .map((device) => device.phonesConnection?.items || [])
            .reduce((acc, val) => acc.concat(val), []);

         this._device = combinedPhones.filter((device) => {
            if (device.phone !== null) {
               if (device.hasOwnProperty('isArchived')) {
                  return device.phoneType === PhoneTypes.Handset && !device.isArchived;
               } else {
                  return device.phoneType === 'Handset';
               }
            }
            return false;
         });

         this.updateExtensionOptions();
      }
   }

   get devices(): DeviceChipOptions[] {
      return this._device;
   }

   @Input() form: UntypedFormGroup;

   @Input() extensionIdsToExclude: number[] = [];

   @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;

   @ViewChild('groupSelect') groupSelect: BaseStationGroupSelectComponent;

   chipsControl = new UntypedFormControl();

   inputControl = new UntypedFormControl();

   values: ChipInputOption[] = [];

   availableExtensions: DeviceOption[] = [];

   filteredExtensions: DeviceOption[] = [];

   searchTerm: string = '';

   noOptions: string = 'No Extensions Available';

   separatorKeysCodes: number[] = [ENTER, COMMA];

   private _device: DeviceChipOptions[];

   onChange = (values: ChipInputOption[]) => {};

   onTouched: () => void;

   get displayValues(): ChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   ngOnInit() {}

   writeValue(value: IncomingBaseStationData[]): void {
      if (value) {
         const baseStation = value.map((item) => ({
            baseStationHandsetId: item.baseStationHandsetId,
            deviceId: item.deviceId,
            phoneId: item.phoneId,
            sortId: item.sortId,
            phone: {
               extension: {
                  number: item.phone.extension.number,
                  name: item.phone.extension.name,
               },
            },
            handsetIpei: item.phone.handsetIpei,
            extension: null,
            isDeleted: false,
         }));

         this.values = [...baseStation];
      } else {
         this.values = [];
      }
      this.chipsControl.setValue(value);

      this.removeErrors();

      this.updateExtensionOptions();
   }
   registerOnChange(onChange: (values: ChipInputOption[]) => void) {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   removeValue(value: ChipInputOption): void {
      const index = this.values.findIndex((v) => v.phoneId === value.phoneId);
      if (index !== -1) {
         if (this.values[index].phoneId === 0) {
            this.values.splice(index, 1);
         } else {
            this.values[index].isDeleted = true;
         }
         this.updateValueAndControls();
         this.validateSortId();
      }
   }

   add(event: MatChipInputEvent): void {
      if (this.groupSelect.isOpen) {
         const value = event.value.trim();
         if (value) {
            const option = this.groupSelect.options.find((o) => o.value?.name?.toLowerCase() === value.toLowerCase());
            if (option && option.value) {
               this.addChip(option.value);
            }
         }
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<DeviceOption>): void {
      if (event.option.value) {
         this.addChip(event.option.value);
      }
   }

   onInputChange(event: Event): void {
      this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
      this.filterOptions(this.searchTerm);
   }

   filterOptions(searchTerm: string): void {
      this.filteredExtensions = this.availableExtensions.filter(
         (extension) =>
            extension.extension.number?.toString().toLowerCase().includes(searchTerm) ||
            extension.extension.name?.toLowerCase().includes(searchTerm) ||
            extension.handsetIpei?.toLowerCase().includes(searchTerm)
      );
      if (this.groupSelect) {
         this.groupSelect.updateFilteredOptions();
      }
   }

   private addChip(value: any): void {
      const existingChipIndex = this.values.findIndex((chip) => chip.phoneId === value.phoneId);
      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }
      const newValue: ChipInputOption = {
         baseStationHandsetId: value.baseStationHandsetId,
         deviceId: value.deviceId,
         phoneId: value.phoneId,
         sortId: value.sortId,
         handsetIpei: value.handsetIpei,
         extension: {
            number: value.extension.number,
            name: value.extension.name,
         },
         phone: null,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.validateSortId();
      this.updateValueAndControls();
   }

   removeErrors(): void {
      if (!this.chipsControl.errors?.required) {
         this.chipsControl.setErrors(null);
      }
   }

   updateExtensionOptions() {
      const selectedPhoneIds = new Set(this.displayValues.map((v) => v.phoneId));
      this.availableExtensions = this.devices.filter(
         (extension) =>
            !selectedPhoneIds.has(extension.phoneId) && !this.extensionIdsToExclude.includes(extension.phoneId)
      );
      this.filteredExtensions = this.availableExtensions;
   }

   updateValueAndControls(): void {
      if (this.groupSelect.panel) {
         this.groupSelect.panel.visible = false;
      }

      this.chipInput.nativeElement.value = '';

      this.inputControl.setValue(null);

      this.chipsControl.setValue(this.displayValues);

      this.onChange(this.values);

      this.removeErrors();
      this.updateExtensionOptions();
      this.validateSortId();
   }

   getChipDisplay(value: ChipInputOption): string {
      const device = value;
      if (device.phone?.extension.number)
         return `${device.phone.extension.number} - ${device.phone.extension.name} (${device.handsetIpei})`;
      else if (device.extension?.number) {
         return `${device.extension.number} - ${device.extension.name} (${device.handsetIpei})`;
      }
      return '';
   }

   validateSortId() {
      let sortIndex = 1;

      this.values = this.values.map((step) => {
         if (!step.isDeleted) {
            return {
               ...step,
               sortId: sortIndex++,
               sequence: sortIndex - 1,
               testSomething: sortIndex,
            };
         }
         return step;
      });
      this.onChange(this.values);
   }
}
